/**
 * Simulation creator component - Terminal aesthetic
 */

import { useState, FormEvent, useEffect } from 'react';
import { createSimulation, getTopologies } from '../services/api';
import type { Simulation, Topology, AIModel } from '../types/simulation';
import './SimulationCreator.css';

interface SimulationCreatorProps {
  onSimulationCreated: (simulation: Simulation) => void;
  onCancel: () => void;
}

export function SimulationCreator({ onSimulationCreated, onCancel }: SimulationCreatorProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status] = useState<'active' | 'maintenance' | 'private'>('active');
  const [isPrivate, setIsPrivate] = useState(false);
  const [topologies, setTopologies] = useState<Topology[]>([]);
  const [aiModels, setAiModels] = useState<AIModel[]>([
    { ai_name: '', topology_id: '', position: null, auto_spawn: true }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingTopologies, setIsLoadingTopologies] = useState(true);

  // Load topologies on mount
  useEffect(() => {
    const loadTopologies = async () => {
      try {
        const data = await getTopologies(true);
        setTopologies(data);
      } catch (err) {
        setError('Failed to load topologies');
        console.error('Failed to load topologies:', err);
      } finally {
        setIsLoadingTopologies(false);
      }
    };
    loadTopologies();
  }, []);

  const addAiModel = () => {
    setAiModels([...aiModels, { ai_name: '', topology_id: '', position: null, auto_spawn: true }]);
  };

  const removeAiModel = (index: number) => {
    setAiModels(aiModels.filter((_, i) => i !== index));
  };

  const updateAiModel = (index: number, field: keyof AIModel, value: any) => {
    const updated = [...aiModels];
    updated[index] = { ...updated[index], [field]: value };
    setAiModels(updated);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Simulation name is required');
      return;
    }

    // Validate AI models
    const validModels = aiModels.filter(m => m.ai_name.trim() && m.topology_id);
    if (validModels.length === 0) {
      setError('At least one AI model is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const newSimulation = await createSimulation({
        name: name.trim(),
        description: description.trim() || undefined,
        status: isPrivate ? 'private' : status,
        allowed_users: isPrivate ? [] : null,
        ai_models: validModels,
      });

      onSimulationCreated(newSimulation);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create simulation');
      console.error('Failed to create simulation:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingTopologies) {
    return (
      <div className="simulation-creator">
        <div className="creator-header">
          <h2>CREATE NEW SIMULATION</h2>
        </div>
        <div className="creator-loading">
          <p>&gt; LOADING TOPOLOGIES...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="simulation-creator">
      <div className="creator-header">
        <h2>CREATE NEW SIMULATION</h2>
      </div>

      <form className="creator-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="sim-name">
            &gt; SIMULATION NAME *
          </label>
          <input
            id="sim-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter simulation name..."
            disabled={isSubmitting}
            maxLength={100}
            autoFocus
          />
        </div>

        <div className="form-group">
          <label htmlFor="sim-description">
            &gt; DESCRIPTION
          </label>
          <textarea
            id="sim-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter simulation description..."
            disabled={isSubmitting}
            rows={3}
            maxLength={500}
          />
        </div>

        <div className="form-group">
          <label>
            &gt; VISIBILITY
          </label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                checked={!isPrivate}
                onChange={() => setIsPrivate(false)}
                disabled={isSubmitting}
              />
              Public
            </label>
            <label>
              <input
                type="radio"
                checked={isPrivate}
                onChange={() => setIsPrivate(true)}
                disabled={isSubmitting}
              />
              Private
            </label>
          </div>
        </div>

        <div className="form-section">
          <div className="section-header">
            <label>&gt; AI MODELS *</label>
            <button
              type="button"
              className="btn-small"
              onClick={addAiModel}
              disabled={isSubmitting}
            >
              + ADD AI MODEL
            </button>
          </div>

          {aiModels.map((model, index) => (
            <div key={index} className="ai-model-config">
              {aiModels.length > 1 && (
                <div className="model-config-header">
                  <button
                    type="button"
                    className="btn-remove"
                    onClick={() => removeAiModel(index)}
                    disabled={isSubmitting}
                  >
                    REMOVE
                  </button>
                </div>
              )}

              <div className="form-group">
                <label>AI Name *</label>
                <input
                  type="text"
                  value={model.ai_name}
                  onChange={(e) => updateAiModel(index, 'ai_name', e.target.value)}
                  placeholder="e.g., Alice_AI"
                  disabled={isSubmitting}
                  maxLength={50}
                />
              </div>

              <div className="form-group">
                <label>Topology *</label>
                <select
                  value={model.topology_id}
                  onChange={(e) => updateAiModel(index, 'topology_id', e.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="">Select a topology...</option>
                  {topologies.map((topo) => (
                    <option key={topo.id} value={topo.id}>
                      {topo.name} {topo.description ? `- ${topo.description}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={model.auto_spawn}
                    onChange={(e) => updateAiModel(index, 'auto_spawn', e.target.checked)}
                    disabled={isSubmitting}
                  />
                  Auto-spawn on simulation start
                </label>
              </div>
            </div>
          ))}
        </div>

        {error && (
          <div className="creator-error">
            <p>ERROR: {error}</p>
          </div>
        )}
      </form>

      <div className="creator-actions">
        <div
          className={`btn-secondary ${isSubmitting ? 'disabled' : ''}`}
          onClick={isSubmitting ? undefined : onCancel}
        >
          cancel
        </div>
        <div
          className={`btn-primary ${isSubmitting ? 'disabled' : ''}`}
          onClick={isSubmitting ? undefined : handleSubmit}
        >
          {isSubmitting ? 'creating...' : 'create simulation'}
        </div>
      </div>
    </div>
  );
}
